import { Doc_Progress, pdq8, fbimod, mapDocTypeToTaskActionTypes } from 'neuro-schemas';
import { pick } from 'ramda';
import { filterByDocumentType, isByInformant, omitControlProps } from 'Utility/documentHandling';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Warning } from '@mui/icons-material';
import { assertCapabilities, getStore } from 'Store/storeRef';
import PlatformCapabilities from '../../../config/capabilities';
import { isNumber, calculateDaysDifference, partialDateFromDate } from 'neuro-utils';
import { isEmpty } from 'Utility/ramdaReplacement';
import { epilepsyDiagnoses, sleepApneaDiagnoses } from 'Routes/Diagnosis/utils/definitions';
import { EN_DASH } from '../../../config/constants';
import { informantSurveysByPlatform, surveysByPlatform } from '../Document/config';
import { DateTime } from 'luxon';
import type { MyServiceContextType } from '../Document';
import { formatPartialDate } from 'Utility/date';
import Censorable from 'Components/Censorable';

const TASK_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss' as const;

export const formatScore = (params: { yielded: number | null; ceiling: number | null }): string => {
  const { yielded, ceiling } = params;
  if (!isNumber(yielded) && !isNumber(ceiling)) return EN_DASH;
  if (!isNumber(yielded) && isNumber(ceiling)) return `${EN_DASH} / ${ceiling}`;
  if (isNumber(yielded) && !isNumber(ceiling)) return String(yielded);
  if (isNumber(yielded) && isNumber(ceiling)) return `${yielded} / ${ceiling}`;
  return EN_DASH;
};

// Sanitize bdi document for calculator
export const bdiIncludeOnlyNumbers = (bdi: IBDI): IBDI => {
  const keys = Object.keys(bdi);
  const numberkeys: Array<keyof IBDI> = keys.filter(k => !isNaN(parseInt(k))) as Array<keyof IBDI>;
  numberkeys.push('date');
  return pick(numberkeys, bdi);
};

const typeIsSurveyType = (type: string): type is string =>
  Object.values(surveysByPlatform)
    .flat()
    .includes(type as TMyDocumentType);

/** @deprecated TODO replace with the completeness-approach */
const getSurveyStatus = (doc: IDocumentWithTypeV2<TMyDocumentType | 'ninmtTreatmentPeriod'>): boolean | undefined => {
  const differentSectionDocuments = ['iadl', 'fbimod'];
  if (typeof doc !== 'object' || !(typeIsSurveyType(doc._type) || differentSectionDocuments.includes(doc._type)))
    return undefined;

  const type = doc._type;
  const data = omitControlProps(doc);

  return Doc_Progress.calculateProgress(type, data).state === Doc_Progress.State.Completed;
};

const makeDocumentHeader = (type: MyServiceContextType) => {
  switch (type) {
    case '_invite':
      return 'myService.invite';
    case '_tasklist':
      return 'myService.taskList';
    case 'bdi':
      return 'myService.ninmt.bdi';
    case 'bai':
      return 'myService.ninmt.bai';
    case 'ninmtPreInquiry':
      return 'myService.ninmt.ninmtPreInquiry.title';
    case 'contraIndicationsInquiry':
      return 'myService.ninmt.contraIndicationsInquiry';
    case 'treatmentMonitoringInquiry':
      return 'myService.ninmt.treatmentMonitoringInquiry.title';
    case 'tdcsReport':
      return 'myService.ninmt.tdcsReport';
    case 'treatmentEfficiency':
      return 'myService.ninmt.treatmentEfficiency';
    case 'supplyShortagesInquiry':
      return 'myService.ninmt.supplyShortagesInquiry';
    case 'copd':
      return 'myService.sleepApnea.copd.title';
    case 'deps':
      return 'myService.sleepApnea.deps.title';
    case 'des':
      return 'myService.sleepApnea.des.title';
    case 'dss':
      return 'myService.sleepApnea.dss.title';
    case 'ess':
      return 'myService.sleepApnea.ess.title';
    case 'isi':
      return 'myService.sleepApnea.isi.title';
    case 'sleepApneaFirstVisitInquiry':
      return 'myService.sleepApnea.sleepApneaFirstVisitInquiry.title';
    case 'deviceTherapyControlVisitInquiry':
      return 'myService.sleepApnea.deviceTherapyControlVisitInquiry.title';
    case 'satisfactionWithCare':
      return 'myService.sleepApnea.satisfactionWithCare.title';
    case 'eq5d':
      return 'myService.sleepApnea.eq5d.title';
    case 'pedsql':
      return 'myService.sma.pedsql';
    case 'nmsq':
      return 'myService.parkinson.nmsq';
    case 'impulseControlSurvey':
      return 'myService.parkinson.impulseControlSurvey';
    case 'mdsUpdrsIV':
      return 'myService.parkinson.mdsUpdrsIV';
    case 'pdq8':
      return 'myService.parkinson.pdq8';
    case 'memory':
      return 'myService.parkinson.memory';
    case 'gds15':
      return 'myService.parkinson.gds15';
    case 'passQuestion':
      return 'myService.mgravis.passQuestion';
    case 'mgadl':
      return 'myService.mgravis.opts.mgadl';
    case 'mgQol15r':
      return 'myService.mgravis.mgQol15r';
    case 'fss':
      return 'myService.mgravis.opts.fss';
    default:
      return 'myService.title';
  }
};

const makeDocumentEditingInfotext = (type: string, sentInvites: boolean) => {
  switch (type) {
    case 'taskList':
      return 'myService.taskListInfoText';
    case 'invite':
      return sentInvites ? 'myService.inviteInformationTextShort' : 'myService.inviteInformationText';
    default:
      return undefined;
  }
};

const makeDocumentInfotext = (
  platform?: Platform,
  otherProps?: { epilepsySeizuresNoNameByPatient?: boolean; docType?: TDocumentType; docData?: any; locale?: Locale },
): React.JSX.Element | undefined | string => {
  if (otherProps?.docType === 'pdq8' && otherProps.locale)
    return <p>{pdq8.localizations[otherProps.locale]['titleDesc']}</p>;
  switch (platform) {
    case 'epilepsy': {
      return otherProps?.epilepsySeizuresNoNameByPatient ? (
        <div style={{ display: 'flex' }}>
          <span style={{ margin: '0 0.5rem 0 0.3rem' }}>
            <Warning color="warning" />
          </span>
          <div>
            <FormattedMessage id="myService.epilepsy.epilepsySeizuresNoNameByPatient" />{' '}
            <FormattedMessage id="diagnosis.epilepsy.seizureType.seizureNameByPatientInfotext" />{' '}
            <FormattedMessage id="myService.epilepsy.epilepsySeizuresNoNameByPatient2" />
          </div>
        </div>
      ) : undefined;
    }
    case 'dmd':
    case 'sma':
      if (otherProps?.docType === 'pedsql') {
        if (otherProps.docData && 'module' in otherProps.docData && otherProps.docData.module === 'fatigue') {
          return `myService.${platform}.fatigue`;
        }
        if (otherProps.docData && 'module' in otherProps.docData && otherProps.docData.module === 'neuromuscular') {
          return `myService.${platform}.neuromuscular`;
        }
      }
      return undefined;
    default:
      return undefined;
  }
};

const toUpperCase = <T extends string = string>(str: T): Uppercase<T> => str.toUpperCase() as Uppercase<T>;

const getPlatformInviteFlavorString = (): null | `INVITE_DOWNLOAD_APP_${Uppercase<Platform>}` => {
  const platform = getStore().getState().session.platforms?.selected;
  if (!platform) return null;
  return `INVITE_DOWNLOAD_APP_${toUpperCase(platform)}`;
};

const matchesInviteFlavorString = (flavor: string) => {
  return flavor.substring(0, 19) === 'INVITE_DOWNLOAD_APP';
};

const checkMyServCapability = (platform: Platform, capabilityGroups: IGroupACLMapping): boolean => {
  const myServTileCapa = assertCapabilities([PlatformCapabilities.MYSERVICE_TILE], capabilityGroups);
  switch (platform) {
    case 'ninmt': {
      const myNinmtCapa = assertCapabilities([PlatformCapabilities.NINMT_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && myNinmtCapa;
    }
    case 'sleepApnea': {
      const mySRCapa = assertCapabilities([PlatformCapabilities.SR_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && mySRCapa;
    }
    case 'epilepsy': {
      const myEpilepsyCapa = assertCapabilities([PlatformCapabilities.EPILEPSY_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && myEpilepsyCapa;
    }
    case 'sma': {
      const mySmaCapa = assertCapabilities([PlatformCapabilities.SMA_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && mySmaCapa;
    }
    case 'dmd': {
      const myDmdCapa = assertCapabilities([PlatformCapabilities.DMD_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && myDmdCapa;
    }
    case 'parkinson': {
      const myParkCapa = assertCapabilities([PlatformCapabilities.PARKINSON_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && myParkCapa;
    }
    case 'mgravis': {
      const myMgravisCapa = assertCapabilities([PlatformCapabilities.MGRAVIS_MYSERVICE_TILE], capabilityGroups);
      return myServTileCapa && myMgravisCapa;
    }
  }
  return myServTileCapa;
};

/**
 * Checks if myService is available in toolbar and clickable in tile
 * @param {Platform} platform
 * @param {boolean} hasCapability Capability of the specific platform given as argument
 * @param docs All docs
 * @returns {boolean}
 */
const isMyServiceAvailable = (
  platform: Platform,
  hasCapability: boolean,
  docs: IDocumentV2[] | undefined = [],
): boolean => {
  switch (platform) {
    case 'ninmt': {
      const indicationForTreatmentDocs = filterByDocumentType(docs, 'indicationForTreatment');
      return hasCapability && indicationForTreatmentDocs.some(doc => doc.indication && !isEmpty(doc.indication));
    }
    case 'sleepApnea': {
      const diagnosisDocs = filterByDocumentType(docs, 'diagnosis');
      if (hasCapability) {
        if (diagnosisDocs.some(d => d.diagnosis && (sleepApneaDiagnoses as string[]).includes(d.diagnosis))) {
          return true;
        }
      }
      return false;
    }
    case 'epilepsy': {
      const seizureTypeDocs = filterByDocumentType(docs, 'seizureType');
      const diagnosisDocs = filterByDocumentType(docs, 'diagnosis');
      if (hasCapability) {
        if (seizureTypeDocs.some(d => d.nameByPatient)) return true;
        if (diagnosisDocs.some(d => d.diagnosis && (epilepsyDiagnoses as string[]).includes(d.diagnosis))) return true;
      }
      return false;
    }
    case 'ms': {
      return hasCapability;
    }
    case 'mgravis': {
      return hasCapability;
    }
    default: {
      return true;
    }
  }
};

const getMySQUsageStatus = (
  mysqUserId: string | null | undefined,
  delegates: Array<IDelegate> | undefined,
): 'patient' | 'delegate' | undefined => {
  if (mysqUserId) return 'patient';
  if (delegates && delegates.length > 0) return 'delegate';
  return;
};

export const getTaskActionTypeOptions = (
  platform: Platform | undefined,
  isInformantTask: boolean,
): TTaskActionType[] => {
  if (!platform) return [];
  const surveys = (isInformantTask ? informantSurveysByPlatform[platform] : surveysByPlatform[platform]) ?? [];
  return surveys
    .filter(survey => {
      // mdsUpdrsIV cannot be filled by patient for now
      if (platform === 'parkinson' && survey === 'mdsUpdrsIV') return false;
      // Treatment efficiency tasks can only be sent from RTMS/TDCS forms
      if (platform === 'ninmt' && survey === 'treatmentEfficiency') return false;
      return true;
    })
    .flatMap(survey => mapDocTypeToTaskActionTypes(survey));
};

export const formatTaskActionType = (params: {
  fm: (id: string, n?: string | number) => string;
  tasklist: Partial<ITaskList>;
  option: string | number;
  latestContraIndicationDocument: IDocumentWithTypeV2<'contraIndicationsToTreatment'> | undefined;
  recurringDisabledOpts: string[];
  platform: Platform | undefined;
  locale: string;
  informantVersion: boolean;
}): string => {
  const {
    fm,
    tasklist,
    option,
    latestContraIndicationDocument,
    recurringDisabledOpts,
    platform,
    locale = 'fi',
    informantVersion,
  } = params;
  switch (option) {
    case 'fbimod': {
      return fbimod.getLocalizations(locale).shortTitle;
    }
    default: {
      const addAsterisk = tasklist.recurrence?.recurring && recurringDisabledOpts.includes(`${option}`);
      if (option === 'contraIndicationsInquiry' && !tasklist.recurrence?.recurring) {
        const end = tasklist.end_exclusive ? DateTime.fromFormat(tasklist.end_exclusive, TASK_DATE_FORMAT) : undefined;
        return `${fm(`myService.ninmt.opts.${option}`)} ${fm(
          'myService.ninmt.opts.contraIndicationsInquiryInfo',
          calculateDaysDifference(
            latestContraIndicationDocument?.date,
            end ? [end.year, end.month, end.day] : undefined,
          ) ?? 'X',
        )}${addAsterisk ? '*' : ''}`;
      }
      if (option === 'memory' && informantVersion) {
        return `${fm('myService.parkinson.memoryInformant')}${addAsterisk ? '*' : ''}`;
      }
      return `${fm(`myService.${platform}.opts.${option}`)}${addAsterisk ? '*' : ''}`;
    }
  }
};

export const hasRequiredTasklistData = ({ tasklist }: { tasklist: Partial<ITaskList> }): boolean => {
  const { template_id } = tasklist;
  if (!template_id) return false;
  return true;
};

export const isTasklist = (tasklist: Partial<ITaskList>): tasklist is ITaskList => {
  return !!(tasklist.template_id && tasklist.start_inclusive);
};

type ClassifiedTasklist = { tasklist: ITaskList; isActiveOrUpcoming: boolean; platform: Platform | undefined };

export const classifyTasklists = ({
  tasklists,
  availablePlatforms,
}: {
  tasklists: ITaskList[];
  availablePlatforms: Platform[];
}): ClassifiedTasklist[] => {
  return tasklists
    .map(tasklist => {
      const isActiveOrUpcoming = true;
      const platform = tasklist.platform;
      return { tasklist, isActiveOrUpcoming, platform };
    })
    .filter(({ platform }) => (platform ? availablePlatforms.includes(platform) : true));
};

// TODO: Move to a more general location since these are used in other sections too
export const surveyCreator = (
  document: Omit<IDocumentV2, keyof IControlProps> & Partial<IControlProps>,
  mysqUserId: string | null | undefined,
  users: ICompactVaultUser[],
  delegates: IDelegate[],
  informants: IInformant[],
  fm: (id: string) => string,
): string | React.JSX.Element => {
  if (document._creator === mysqUserId) return fm('myService.patient');

  const delegate = delegates.find(del => del.id === document._creator);
  if (delegate) {
    // PedsQL if patient filled with parents phone
    if ('type' in document && document.type === 'selfReport') {
      return fm('myService.patient');
    }
    return <Censorable>{`${delegate.first_names} ${delegate.last_names}`}</Censorable>;
  }

  if (isByInformant(document, informants)) {
    const informant = informants.find(inf => inf.mysqUserId === document._creator);
    if (informant) {
      return <Censorable>{`${informant.firstNames} ${informant.lastNames} (${fm('delegates.informant')})`}</Censorable>;
    }
    return fm('delegates.informant');
  }

  const hcpCreator = users.find(u => u.userId === document._creator);
  if (hcpCreator) return `${hcpCreator.firstNames} ${hcpCreator.lastNames}`;

  return fm('myService.hcp');
};

// TODO: Move to a more general location since these are used in other sections too
export const surveyEditor = (
  document: Omit<IDocumentV2, keyof IControlProps> & Partial<IControlProps>,
  mysqUserId: string | null | undefined,
  users: ICompactVaultUser[],
  delegates: Array<IDelegate>,
  informants: Array<IInformant>,
  fm: (id: string) => string,
): string | React.JSX.Element => {
  if (!document._commitsLength || document._commitsLength === 1) return '';

  const commitPartialDate = formatPartialDate(partialDateFromDate(new Date(document._cdate ?? 0)));

  if (document._commitCreator === mysqUserId) return `${fm('myService.patient')} (${commitPartialDate})`;

  const delegate = delegates.find(del => del.id === document._commitCreator);
  if (delegate) {
    // PedsQL if patient filled with parents phone
    if ('type' in document && document.type === 'selfReport') {
      return fm('myService.patient');
    }
    return <Censorable>{`${delegate.first_names} ${delegate.last_names} (${commitPartialDate})`}</Censorable>;
  }

  const informant = informants.find(inf => inf.mysqUserId === document._commitCreator);
  if (informant) {
    return (
      <Censorable>{`${informant.firstNames} ${informant.lastNames} (${fm('delegates.informant')}) (${commitPartialDate})`}</Censorable>
    );
  }

  const hcpCreator = users.find(u => u.userId === document._commitCreator);
  if (hcpCreator) return `${hcpCreator.firstNames} ${hcpCreator.lastNames} (${commitPartialDate})`;

  // If the commit came from IROM and _commitCreator is not found on other lists, assume (deleted) IROM editor
  if (document._commitSource === 'irom') return `${fm('delegates.informant')} (${commitPartialDate})`;

  return `${fm('myService.hcp')} (${commitPartialDate})`;
};

export {
  getSurveyStatus,
  makeDocumentHeader,
  makeDocumentEditingInfotext,
  makeDocumentInfotext,
  getPlatformInviteFlavorString,
  matchesInviteFlavorString,
  isMyServiceAvailable,
  checkMyServCapability,
  getMySQUsageStatus,
};
